import React from "react"
import { twMerge } from "tailwind-merge"

import {
  prefersReducedMotion,
  useScrollReveal,
  type ScrollRevealVariant
} from "../../hooks/useScrollReveal"
import {
  getScrollRevealTransitionClass,
  SCROLL_REVEAL_STAGGER_MS,
  scrollRevealHiddenClasses,
  scrollRevealVisibleClasses
} from "./scrollRevealVariants"

export type { ScrollRevealVariant }

type ScrollRevealProps = {
  children: React.ReactNode
  className?: string
  variant?: ScrollRevealVariant
  threshold?: number
  rootMargin?: string
  disabled?: boolean
  /** Stagger delay in ms (e.g. index * stagger). */
  delayMs?: number
  as?: "div" | "section" | "article" | "header" | "footer"
  style?: React.CSSProperties
}

export function ScrollReveal({
  children,
  className,
  variant = "fadeUp",
  threshold,
  rootMargin,
  disabled = false,
  delayMs = 0,
  as: Component = "div",
  style
}: ScrollRevealProps): React.ReactElement {
  const reducedMotion = disabled || prefersReducedMotion()
  const [isVisible, ref] = useScrollReveal({ threshold, rootMargin, disabled: reducedMotion })

  if (variant === "none" || reducedMotion) {
    return (
      <Component className={className} style={style}>
        {children}
      </Component>
    )
  }

  const hidden = scrollRevealHiddenClasses[variant]
  const visible = scrollRevealVisibleClasses[variant]
  const transitionClass = getScrollRevealTransitionClass(reducedMotion)

  const motionClassName = twMerge(transitionClass, isVisible ? visible : hidden, className)
  const motionStyle: React.CSSProperties = {
    ...style,
    transitionDelay: delayMs > 0 && !reducedMotion ? `${delayMs}ms` : undefined
  }

  return (
    <Component ref={ref} className={motionClassName} style={motionStyle}>
      {children}
    </Component>
  )
}

type ScrollRevealItemProps = Omit<ScrollRevealProps, "delayMs"> & {
  staggerIndex?: number
  staggerDelayMs?: number
  /** Overrides stagger math when set (e.g. hero headline timing). */
  entranceDelayMs?: number
}

/** Child item with index-based stagger (Tribe-style row/card reveals). */
export function ScrollRevealItem({
  staggerIndex = 0,
  staggerDelayMs = SCROLL_REVEAL_STAGGER_MS,
  entranceDelayMs,
  ...props
}: ScrollRevealItemProps): React.ReactElement {
  const delayMs = entranceDelayMs ?? staggerIndex * staggerDelayMs
  return <ScrollReveal {...props} delayMs={delayMs} />
}
